import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  AddBoxOutlined,
  PictureAsPdf,
  FileDownloadOutlined,
  DeleteOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import type { PaginatedResponse } from "../../../stream-management/components/StreamProgram";

interface StreamProgram {
  id: number;
  file?: string;
  created_at: string;
  updated_at: string;
}

interface StreamProgramPanelProps {
  streamId?: number;
}

const ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.doc,.docx,.ppt,.pptx,application/pdf,image/png,image/jpeg";

const getFileName = (url?: string) => {
  if (!url) return "Program";
  return decodeURIComponent(url.split("/").pop() || "Program");
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const StreamProgramPanel: React.FC<StreamProgramPanelProps> = ({ streamId }) => {
  const [programs, setPrograms] = useState<StreamProgram[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const fetchPrograms = useCallback(async () => {
    if (!streamId) return;
    try {
      const res = await apiService.get<PaginatedResponse<StreamProgram>>(
        `/api/streams/stream-program/?stream=${streamId}`,
      );
      setPrograms(res.data.results ?? []);
    } catch {
      // silently fail — sidebar should not block the page
    }
  }, [streamId]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  const handleUpload = async (file: File) => {
    if (!streamId) return;
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("stream_id", String(streamId));
      formData.append("file", file);
      await apiService.post("/api/streams/stream-program/", formData);
      toast.success("Program uploaded");
      await fetchPrograms();
    } catch {
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Delete this program?")) return;
    try {
      await apiService.delete(`/api/streams/stream-program/${id}/`);
      toast.success("Deleted");
      setPrograms((prev) => prev.filter((p) => p.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleDownload = (url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(url, "_blank");
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "20px",
        p: 2.5,
        boxShadow: "none",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ color: "#000" }}>
          Stream Program
        </Typography>

        <Tooltip title="Upload program">
          <span>
            <IconButton
              onClick={() => inputRef.current?.click()}
              disabled={isUploading}
              size="small"
              sx={{
                border: "1.5px solid",
                borderColor: "divider",
                borderRadius: "10px",
                width: 36,
                height: 36,
                color: "text.primary",
              }}
            >
              {isUploading ? (
                <CircularProgress size={16} />
              ) : (
                <AddBoxOutlined sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUpload(f);
          }}
        />
      </Box>

      {/* Document list */}
      {programs.length === 0 ? (
        <Box
          sx={{
            py: 3,
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          <Typography variant="body1">No programs uploaded yet</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {programs.map((program) => {
            const name = getFileName(program.file);
            const date = formatDate(program.updated_at);
            const isPdf =
              program.file?.toLowerCase().endsWith(".pdf") ?? false;

            return (
              <Box
                key={program.id}
                onClick={() =>
                  program.file && window.open(program.file, "_blank")
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: "12px",
                  backgroundColor: "#f5f7fa",
                  cursor: program.file ? "pointer" : "default",
                  "&:hover": {
                    backgroundColor: "#edf0f4",
                  },
                }}
              >
                {/* File type icon */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    backgroundColor: isPdf ? "#fee2e2" : "#dbeafe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PictureAsPdf
                    sx={{
                      fontSize: 22,
                      color: isPdf ? "#ef4444" : "#3b82f6",
                    }}
                  />
                </Box>

                {/* Name + date */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    fontWeight="600"
                    noWrap
                    sx={{ color: "#111" }}
                  >
                    {name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                    {date}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  {program.file && (
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        onClick={(e) => handleDownload(program.file!, e)}
                        sx={{ color: "#9ca3af" }}
                      >
                        <FileDownloadOutlined sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(program.id, e)}
                      sx={{ color: "#9ca3af", "&:hover": { color: "#ef4444" } }}
                    >
                      <DeleteOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default StreamProgramPanel;
